import { kbaEndpoints } from '@/plugins/api/endpoints/kba'
import { physicianEndpoints } from '@/plugins/api/endpoints/physician'
import { senEmailEndpoints } from '@/plugins/api/endpoints/sendEmailEndpoins'
import { treatmentEndpoints } from '@/plugins/api/endpoints/treatment'
import { validationsEndpoints } from '@/plugins/api/endpoints/validationEndpoints'
import router from '@/router'
import store from '@/store'
import { useAuthStore } from '@/store/pinia/auth'
import { useLogoutQueueStore } from '@/store/pinia/logout-queue'
import { GetShortwayDashboardOutputDto } from '@/types/dashboard'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as am4core from '@amcharts/amcharts4/core'
import axios from 'axios'
import { util } from '../util'
import { addressEndpoints } from './endpoints/addressEndpoint'
import { attendanceEndpoints } from './endpoints/attendance'
import { authEndpoints } from './endpoints/auth'
import { CalculatorsEndpoints } from './endpoints/calculator'
import { clinicEndpoints } from './endpoints/clinic'
import { clinicTaskforceLinkEndpoints } from './endpoints/clinicTaskforceLink'
import { cpfEndpoints } from './endpoints/cpf'
import { diseaseEndpoints } from './endpoints/disease'
import { attendanceFormEndpoints } from './endpoints/guided-attendance-form'
import { lmeEndpoints } from './endpoints/lme'
import { medicalDocumentEndpoints } from './endpoints/medical-document'
import { metabaseQuestionEndpoints } from './endpoints/metabase-question'
import { parameterEndpoints } from './endpoints/parameter'
import { patientEndpoints } from './endpoints/patient'
import { printingTemplateEndpoints } from './endpoints/printing-template'
import { taskforceEndpoints } from './endpoints/taskforce'
import { termEndpoints } from './endpoints/term'
import { APIError } from './error'

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 1000 * 60 * 60,
})

export function requestNewAccessToken() {
  return apiClient.post('/refresh-access-token', {}, { withCredentials: true })
}

apiClient.interceptors.response.use(
  (response) => response.text || response.data,
  async (error) => {
    if (!error.response) {
      throw new APIError({
        message: APIError.messageFallback,
        description: 'Não foi possível conectar-se ao servidor! Verifique sua conexão e tente novamente.',
        code: 503,
      })
    }

    if (error.response.status === 404 && error.response.data === 'ROUTE_NOT_FOUND') {
      throw new APIError({
        message: 'Rota não encontrada!',
        description: `A rota '[${error.config.method?.toUpperCase()}] ${
          error.config.url
        }' não foi mapeada corretamente. Entre em contato com o Suporte!`,
        code: 404,
      })
    }

    if (error.response.status === 401 && error.response.data.slug === 'TokenExpiredError') {
      const { token } = await requestNewAccessToken()

      await store.dispatch('auth/setToken', token)

      return await apiClient.request({
        ...error.response.config,
        url: error.response.request.responseURL,
      })
    }

    if (error.response.status === 401 && error.response.data.slug === 'InvalidSession') {
      await util.waitSomeTime(500)
      return await apiClient.request({
        ...error.response.config,
        url: error.response.request.responseURL,
        headers: {
          ...(error.response.config.headers ?? {}),
          'session-retry': (parseInt(error.response.config.headers['session-retry']) || 0) + 1,
        },
      })
    }

    if (error.response.status === 401) {
      const logoutQueueStore = useLogoutQueueStore()
      const authStore = useAuthStore()

      const unauthorizedLogout = async () => {
        const sessionId = store.getters['auth/sessionId']
        if (sessionId) {
          try {
            await apiClient.patch(
              `logout/${sessionId}`,
              {},
              { withCredentials: true, params: { clearActiveSession: true } },
            )
          } catch (error) {
            console.error(error)
          }
        }
        await store.dispatch('auth/clearCredentials', null, { root: true })
        authStore.setToken(null)
        store.commit('auth/setCurrentHomePage', null, { root: true })
        authStore.setCurrentHomePage(null)
        setTimeout(() => {
          router.push('/login').catch((_failure) => {})
        }, 100)
      }

      logoutQueueStore.enqueue(unauthorizedLogout)

      throw new APIError({
        message: 'Sessão expirada!',
        description: 'Sua sessão expirou. Tente fazer login novamente.',
        code: 401,
      })
    }

    if (error.response.data) {
      throw new APIError({
        message: error.response.data.error.title,
        description: error.response.data.error.message,
        cause: error.response.data.error.cause,
        code: error.response.data.code,
        slug: error.response.data.slug,
      })
    }

    throw new APIError({
      message: APIError.messageFallback,
      description: APIError.descriptionFallback,
      cause: error,
      code: 500,
    })
  },
)

apiClient.interceptors.request.use(
  (config) => {
    if (!config.public) {
      const defaultHeaders = {
        Authorization: `Bearer ${store.state.auth.token}`,
        'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      config.headers = { ...(config.headers || {}), ...defaultHeaders }
    }

    if (config.params) {
      const params = new URLSearchParams()
      for (const key in config.params) {
        if (config.params[key] !== null && config.params[key] !== undefined) {
          params.append(key, config.params[key])
        }
      }
      config.params = params
    }
    return config
  },
  (error) => {
    if (error instanceof Error) {
      throw error
    }

    throw new Error(error)
  },
)

/** @typedef {typeof routes} Api */

export const routes = {
  /**
   * @return {Promise<{
   * token: string;
   * refreshToken: string;
   * expiringAction:string |null
   * }>}
   *
   */
  async login({ login, password, codeToLogin, pharmaceuticalId, chosenUser, checkPasswordExpiration = true }) {
    return apiClient.post(
      `auth`,
      { login, password, codeToLogin, pharmaceuticalId, chosenUser, checkPasswordExpiration },
      { withCredentials: true },
    )
  },
  async logout() {
    return apiClient.patch(
      `logout`,
      {},
      {
        withCredentials: true,
      },
    )
  },
  /**
   * @param {number} pharmaceuticalId
   * @return {Promise<{token: string}>}
   *
   */
  async requestNewAccessTokenWithPharmaceuticalId(pharmaceuticalId) {
    return apiClient.get(`refresh-access-token/${pharmaceuticalId}`, { withCredentials: true })
  },
  /**
   * @param {string} name
   * @return {Promise<{id: number}>}
   *
   */
  async getAnalyticsTypeByName(name) {
    return apiClient.get(`get-analytics-type-by-name?name=${name}`)
  },
  async forgotPassword(login) {
    return apiClient.post(`user/forgot-password`, { login })
  },
  async resetPassword(login, resetPasswordCode, newPassword) {
    return apiClient.post(`user/reset-password`, { login, resetPasswordCode, newPassword })
  },

  /**
   *
   * @param {string} path
   * @param {*} listOptions
   * @returns {Promise<{items:unknown[], total:number}>}
   */
  async listCrud(path, listOptions = {}) {
    return apiClient.get(`${path}`, {
      params: listOptions,
    })
  },
  async removeCrud(path, id) {
    return apiClient.delete(`${path}/${id}`)
  },
  async restoreCrud(path, id) {
    return apiClient.patch(`${path}/${id}/restore`)
  },
  async createCrud(path, data) {
    return apiClient.post(`${path}`, data)
  },
  async updateCrud(path, id, data) {
    return apiClient.put(`${path}/${id}`, data)
  },

  async fetchOptions(path, search, take = 10) {
    return apiClient.get(`options/${path}`, {
      params: { search, take },
    })
  },
  async listClinicPhysicians(path) {
    return apiClient.get(path)
  },

  async createUpdateClinic(payload) {
    return apiClient.post('create-update-clinic', payload)
  },

  async createUpdateInfusionClinic(payload) {
    return apiClient.post('create-update-infusion-clinic', payload)
  },

  async listUf(path) {
    return apiClient.get(path)
  },
  /**
   * @param {{search?:string}} [options={}]
   * @return {Promise<{id: number; name: string; custom: boolean }[]>}
   *
   */
  async listHealthPlan(options = {}) {
    return apiClient.get('/list-health-plan', {
      params: options,
    })
  },
  async createTreatment(treatment) {
    return apiClient.post(`treatment`, treatment)
  },
  async getTreatment(id) {
    return apiClient.get(`treatment/${id}`)
  },
  async patchTreatmentGuidelines(treatmentId, guidelines) {
    return apiClient.patch(`treatment/${treatmentId}/guidelines`, { content: guidelines })
  },
  async patchTreatmentMedicalCareSystem(treatmentId, type, value) {
    return apiClient.patch(`treatment/${treatmentId}/medical-care-availabity/${type}`, {
      content: value,
    })
  },
  async createTreatmentDosage(dosage) {
    return apiClient.post(`treatment/dosage`, dosage)
  },
  async updateTreatmentDosage(id, dosage) {
    return apiClient.put(`treatment/dosage/${id}`, dosage)
  },
  async removeTreatmentDosage(id) {
    return apiClient.delete(`treatment/dosage/${id}`)
  },
  async createTreatmentReference(reference) {
    return apiClient.post(`treatment/reference`, reference)
  },
  async updateTreatmentReference(id, reference) {
    return apiClient.put(`treatment/reference/${id}`, reference)
  },
  async removeTreatmentReference(id) {
    return apiClient.delete(`treatment/reference/${id}`)
  },
  async createTreatmentPrescription(prescription) {
    return apiClient.post(`treatment/prescription`, prescription)
  },
  async updateTreatmentPrescription(id, prescription) {
    return apiClient.put(`treatment/prescription/${id}`, prescription)
  },
  async removeTreatmentPrescription(id) {
    return apiClient.delete(`treatment/prescription/${id}`)
  },
  async loadProfileData() {
    return apiClient.get(`user/profile`)
  },
  async loadPhysicianProfileData() {
    return apiClient.get(`get-physician-profile-info`)
  },
  async saveProfileData(profileData) {
    return apiClient.put(`user/profile`, profileData)
  },
  /**
   *
   * @param { "ASSINATURA" | 'FOTO'} type
   * @returns {Promise<string | null>}
   */
  async loadProfileImage(type) {
    return apiClient.get(`user/profile/${type}`)
  },
  async updateProfileImage(imageType, image) {
    const formData = new FormData()
    formData.append('image', image)
    return apiClient.put(`user/profile/${imageType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  async removeProfileImage(imageType) {
    return apiClient.delete(`user/profile/${imageType}`)
  },
  async getPatientAttendances(patientId, listOptions) {
    return apiClient.get(`attendance/patient/${patientId}`, {
      params: listOptions,
    })
  },
  async getPatientByIdWithAttendances(id) {
    return apiClient.get(`get-patient-by-id-with-attendances/${id}`)
  },

  async upsertPatientAttendancaExam(Attendance) {
    return apiClient.post(`patient-attendance-exam/upsert`, Attendance)
  },

  async removePatientAttendancaExam(id) {
    return apiClient.delete(`patient-attendance-exam/${id}`)
  },

  async getPhysicianShifts(physicianId, listOptions) {
    return apiClient.get(`shift/physician/${physicianId}`, {
      params: listOptions,
    })
  },

  async createNewTaskforceAppointment(path, appointment) {
    return apiClient.post(path, appointment)
  },
  async createPhysicianRegistrationAnalyticsData(uuid) {
    return apiClient.post(`create-phyisician-registration-routing`, { uuid })
  },
  async createAppointment(appointment) {
    return apiClient.post(`appointment`, appointment)
  },

  async sendLastDailyAppointmentReminder(params) {
    return apiClient.post(`send-last-daily-reminder-for-appointment`, params)
  },
  async getAllAppointments(listOptions) {
    return apiClient.get(`appointment`, {
      params: listOptions,
    })
  },
  async createAttendance(attendance) {
    return apiClient.post(`attendance`, attendance)
  },
  async getAttendance(attendanceId) {
    return apiClient.get(`attendance/${attendanceId}`)
  },
  async getAttendanceOverview(attendanceId) {
    return apiClient.get(`attendance/${attendanceId}/overview`)
  },
  async getClinicAppointmentsInRange(clinicId, startDate, endDate) {
    return apiClient.get(`get-clinic-appointments-in-range/${clinicId}/${startDate}/${endDate}`)
  },
  async patchAttendance(attendanceId, { hypothesis, report, references, note, data, summary, weightKg, heightCm }) {
    return apiClient.patch(`attendance/${attendanceId}`, {
      hypothesis,
      report,
      references,
      note,
      data,
      summary,
      weightKg,
      heightCm,
    })
  },
  /**
   *
   * @param {number} attendanceId
   * @returns {Promise<void>}
   */
  async cancelAttendance(attendanceId) {
    return apiClient.delete(`attendance/${attendanceId}`)
  },
  async finishAttendance(finishAttendanceObject) {
    return apiClient.post('finish-attendance', { ...finishAttendanceObject })
  },
  async removeAppointment(id) {
    return apiClient.delete(`appointment/${id}`)
  },
  async getAppointmentsInRange(startDate, endDate) {
    return apiClient.get(`appointment/range/${startDate}/${endDate}`)
  },
  async updateAppointment(id, appointment) {
    return apiClient.put(`appointment/${id}`, appointment)
  },
  async updateAppointmentStatus(id, status) {
    return apiClient.patch(`appointment/${id}/status`, { content: status })
  },
  /**
   * @return {Promise<{
   * items: {
   *  id: number
   *  name: string
   *  active: boolean,
   *  createdAt: string,
   *  description: string,
   *  updatedAt: string,
   * }[];
   * total: string;
   * }>}
   *
   */
  async getDiseases() {
    return apiClient.get(`disease`)
  },
  async getMedicinesForDiseaseTreatment(diseaseId) {
    return apiClient.get(`treatment/disease/${diseaseId}/medicine`)
  },
  /** @returns {Promise<{id: number, name: string}[]>} */
  async getMedicineStopReasons() {
    return this.fetchOptions(`stop-medicine-reason`, null, 0)
  },
  async uploadPatientAttendanceFile(uuid, file, data, type) {
    const formData = new FormData()
    formData.append('data', JSON.stringify(data))
    formData.append('type', type)
    formData.append('image', file)
    return apiClient.post(`patient/${uuid}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  async removePatientAttendanceFile(fileId) {
    return apiClient.delete(`patient/file/${fileId}`)
  },
  async getAttendanceFiles(attendanceId, listOptions) {
    return apiClient.get(`attendance/${attendanceId}/files`, {
      params: listOptions,
    })
  },
  async updatePatientDocumentFile(documentId, payload) {
    return apiClient.put(`update-patient-attendance-document/${documentId}`, payload)
  },
  /**
   *
   * @param {number} attendanceId
   * @param {{ take: number; search: string }} listOptions
   * @returns {Promise<{
   *  items: {
   *    attendanceDate: Date,
   *    attendanceFormId: Number,
   *    attendanceId: Number,
   *    description: string,
   *    disease: string,
   *    extension: string,
   *    id: number,
   *    name: string,
   *    referenceDate: Date,
   *    type: string,
   *    url: string
   *  }[];
   *  total: number
   * }>}
   */
  async getAttendancePhotos(attendanceId, listOptions = {}) {
    return apiClient.get(`attendance/${attendanceId}/photos`, {
      params: listOptions,
    })
  },
  async getAttendanceExams(attendanceId, listOptions) {
    return apiClient.get(`attendance/${attendanceId}/exams`, {
      params: listOptions,
    })
  },
  async removeAttendanceFile(fileId) {
    return apiClient.delete(`attendance/file/${fileId}`)
  },
  /**
   *
   * @param {number} patientId
   * @param {{ take: number; search: string }} listOptions
   * @returns {Promise<{
   *  items: {
   *    attendanceDate: Date,
   *    attendanceFormId: Number,
   *    attendanceId: Number,
   *    description: string,
   *    disease: string,
   *    extension: string,
   *    id: number,
   *    name: string,
   *    referenceDate: Date,
   *    type: string,
   *    url: string
   *  }[];
   *  total: number
   * }>}
   */
  async getPatientAttendancePhotos(patientId, listOptions = {}) {
    return apiClient.get(`patient/${patientId}/photos`, {
      params: listOptions,
    })
  },
  async getPatientDocumentExams(patientId, listOptions) {
    return apiClient.get(`patient/${patientId}/exams`, {
      params: listOptions,
    })
  },
  async getPatientDocumentFiles(patientId, listOptions) {
    return apiClient.get(`patient/${patientId}/files`, {
      params: listOptions,
    })
  },
  async getAttendanceDocumentFiles(attendanceId, listOptions) {
    return apiClient.get(`attendance/${attendanceId}/files`, {
      params: listOptions,
    })
  },

  async getPhysicianMedicalGroups(physicianId) {
    return apiClient.get(`physician/${physicianId}/medical-group`)
  },
  async getAttendanceMedicalGroupsResponsible(attendanceId, responsible) {
    return apiClient.get(`attendance/${attendanceId}/medical-group/${!!responsible}`)
  },
  async updateAttendanceSharedGroups(attendanceId, groups) {
    return apiClient.patch(`attendance/${attendanceId}/medical-group`, groups)
  },
  async getTreatmentsForDisease(diseaseId, mini = false) {
    return apiClient.get(`treatment/disease/${diseaseId}`, { params: { mini } })
  },
  async printTreatmentPrescription(treatmentId, dosageId, isFirstPrescription) {
    return apiClient.get(`treatment/print/${treatmentId}/${dosageId}/${isFirstPrescription}`, {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
      },
    })
  },
  async generateVouchers(pharmaceuticalId, quantity) {
    return apiClient.post('voucher', { pharmaceuticalId, quantity })
  },
  /**
   *
   * @returns  {Promise<{items:{
   * id: number;
   * active: boolean;
   * name: string;
   * type: string;
   * header: string | null;
   * footer: string | null;
   * headerHeight: number | null;
   * footerHeight: number | null;
   * }[]}>}
   */
  async getTemplates() {
    return apiClient.get(`template`)
  },
  async sendSmsUploadPhoto(attendanceId) {
    return apiClient.post(`attendance/${attendanceId}/sms/upload-photo`)
  },
  /**
   *
   * @param {number} attendanceId
   * @returns {Promise<void>}
   */
  async sendSmsAcceptTerms(attendanceId) {
    return apiClient.post(`attendance/${attendanceId}/sms/accept-terms`)
  },
  async sendSmsPatientVideoCall(attendanceId) {
    return apiClient.post(`attendance/${attendanceId}/sms/video-call`)
  },
  async acceptTermsPatient(uuid) {
    return apiClient.patch(`auth/accept-terms/patient`, { content: uuid })
  },
  /**
   *
   * @returns { Promise<{token: string }>}
   */
  async acceptTermsUser() {
    return apiClient.post(`user-accept-term`)
  },
  /**
   *
   * @param {number} pharmaceuticalId
   *  @returns {Promise<{term: string}>}
   */
  async getPharmaceuticalTerm(pharmaceuticalId) {
    return apiClient.get(`get-pharmaceutical-term/${pharmaceuticalId}`)
  },
  async patchTreatmentExhibitionConfig(treatmentId, config) {
    return apiClient.patch(`treatment/${treatmentId}/config`, { content: config })
  },
  async applyVoucherToConsultation(consultationId, voucherCode) {
    return apiClient.patch(`consultation/${consultationId}/apply-voucher/${voucherCode}`)
  },
  async requestVoucherForConsultation(consultationId) {
    return apiClient.post(`consultation/${consultationId}/request-voucher`)
  },
  async sendConsultationMessage(consultationId, message, attachment) {
    const formData = new FormData()
    formData.append('message', message)
    formData.append('consultationId', consultationId)
    formData.append('file', attachment)
    return apiClient.post(`send-consultation-message`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  async createConsultation(specialtyId, attendanceId = null) {
    return apiClient.post(`consultation`, {
      specialtyId,
      attendanceId,
    })
  },
  async listConsultationForPhysician(listOptions) {
    return apiClient.get(`consultation/physician`, { params: listOptions })
  },
  async listConsultationForSpecialist(listOptions) {
    return apiClient.get(`consultation/specialist`, { params: listOptions })
  },
  async listConsultationMessages(consultationId) {
    return apiClient.get(`consultation/${consultationId}/messages`)
  },
  async takeConsultationAsSpecialist(consultationId) {
    return apiClient.patch(`consultation/${consultationId}/take`)
  },
  async checkPatientAcceptedTerms(patientId) {
    return apiClient.get(`patient/${patientId}/accept-terms`)
  },
  async getPrivateMeetingUrl(meetingId) {
    return apiClient.get(`appointment/private-meeting/${meetingId}`)
  },
  async getMeetingUrl(meetingId) {
    return apiClient.get(`appointment/meeting/${meetingId}`)
  },
  async toggleAllowConsultationAppointment(consultationId, value) {
    return apiClient.patch(`consultation/${consultationId}/allow-appointment/${value}`)
  },
  async toggleAllowConsultationPatientFiles(consultationId, value) {
    return apiClient.patch(`consultation/${consultationId}/allow-patient-files/${value}`)
  },
  async finishConsultation(consultationId) {
    return apiClient.patch(`consultation/${consultationId}/finish`)
  },
  async cancelConsultation(consultationId) {
    return apiClient.delete(`consultation/${consultationId}`)
  },
  async getCityByIbgeCode(ibgeCode) {
    return apiClient.get(`city/ibge/${ibgeCode}`)
  },
  async getDashBoard() {
    return apiClient.get(`dashboard/`)
  },
  async getDashboardData() {
    return apiClient.get(`get-dashboard-data`)
  },
  async updateWaitingRoom(id, options) {
    return apiClient.patch(`waiting-room/${id}`, { physicianIn: options.physicianIn })
  },
  async getWaitingRoom(id) {
    return apiClient.get(`waiting-room/${id}`)
  },
  async getWaitingRoomByUuid(uuid) {
    return apiClient.get(`waiting-room/patient/${uuid}`)
  },
  async updateWaitingRoomByUuid(uuid, options) {
    return apiClient.patch(`waiting-room/patient/${uuid}`, { patientIn: options.patientIn })
  },
  async verifyCPF(cpf) {
    return apiClient.post('verify-cpf', { cpf })
  },
  async getPatientByCpf(cpf) {
    return apiClient.get(`patient/cpf/${cpf}`)
  },
  async getTaskforceImages(id) {
    return apiClient.get(`taskforce/${id}/image`)
  },
  async setTaskforceImageAnalysis(taskforceAnswerDocumentId, specialistId, analysis) {
    return apiClient.post(`taskforce/analysis`, { taskforceAnswerDocumentId, specialistId, analysis })
  },

  async getTaskforceEvents(taskforceAnswerId) {
    return apiClient.get(`get-taskforce-appointment-events/${taskforceAnswerId}`)
  },
  async updateTaskforceStatus(id, status, note) {
    //return apiClient.patch(`taskforce/${id}/status`, { status })
    return apiClient.patch(`update-taskforce-answer-status`, { status, taskforceAnswerId: id, note })
  },
  async getTaskforceChatMessages(taskforceAnswerId) {
    return apiClient.get(`taskforce/${taskforceAnswerId}/chat`)
  },
  async sendTaskforceChatMessage(taskforceAnswerId, message, authorId) {
    return apiClient.post(`taskforce/${taskforceAnswerId}/chat`, { message, taskforceAnswerId, authorId })
  },
  async createSurveyFromAttendance(attendanceId, patientId, physicianId, surveyType) {
    return apiClient.post(`create-survey-from-attendance`, { attendanceId, patientId, physicianId, type: surveyType })
  },
  async getSurveyByUuid(surveyUuid) {
    return apiClient.get(`get-survey-by-uuid/${surveyUuid}`)
  },
  async updateSurveyByUuid(surveyUuid, data) {
    return apiClient.post(`update-survey-by-uuid`, { uuid: surveyUuid, data })
  },
  async sendSurveyToPatient(surveys, patientId, physicianId) {
    throw new Error('Not implemented')
  },
  async getPatientSurveys(patientId, physicianId) {
    return apiClient.get('get-patient-surveys', { params: { patientId, physicianId } })
  },
  async getTaskforcePatientAndClinic(taskforceAppointmentUuid) {
    return (
      await axios.get(process.env.VUE_APP_API_URL + '/get-patient-and-clinic-by-uuid?uuid=' + taskforceAppointmentUuid)
    ).data
  },
  async listClinics(options) {
    return apiClient.get('get-clinic-list', { params: options })
  },
  async saveTaskforcePatient(patientInfo) {
    return apiClient.post('save-taskforce-patient', { ...patientInfo })
  },
  async savePatientAvailability(patientId, preferredPeriods) {
    return apiClient.post('save-patient-availability', { patientId, preferredPeriods })
  },
  async getUserPendingSurveys() {
    return apiClient.get(`get-user-pending-survey/`)
  },
  async saveUserSurveyAnswer(surveyId, data, userId) {
    return apiClient.post(`save-user-survey-answer`, { surveyId, data, userId })
  },
  async getPhysicianNameByCrm(crmNumber, crmState) {
    return apiClient.get(`get-physician-by-crm/${crmNumber}/${crmState}`)
  },
  async updateClinicPhysicians(physicians, clinicId) {
    return apiClient.post('clinic-physicians-update', { physicians, clinicId })
  },
  async getPharmaceuticalOriginLocations(pharmaceuticalId) {
    return apiClient.get(`list-pharmaceutical-taskforce-origin-messages`, { params: { pharmaceuticalId } })
  },
  async createClinicTaskforceLink(clinicId, originId) {
    return apiClient.post('create-clinic-taskforce-link', { clinicId, originId })
  },
  async updatePhysicianInfo(info) {
    return apiClient.patch('update-physician-info', { ...info })
  },
  /**
   *
   * @param {profileData: {
   *  id: number;
   *  name?: string;
   *  email?: string;
   *  gender: string | null;
   *  mobile?: string;
   *  birthday: string | null;
   *  crms: {
   *   number: string;
   *   state: string | null;
   *  }[];
   *  cns?: string;
   *  specialties: {
   *   specialtyId: number;
   *   specialty: {
   *    id: number
   *    specialtyName: string
   *    subspecialtyName: string | null
   *    createdAt: string
   *    updatedAt: string
   *    deletedAt: string | null
   *   };
   *   rqe: string | null;
   *  }[] | null;
   *  attendanceAddresses?: {
   *   name: string;
   *   zipCode: string;
   *   mobile: string;
   *   phone: string | null;
   *   street: string;
   *   district: string;
   *   number: string;
   *   complement: string | null;
   *   cityId: number;
   *   city: {
   *     ibgeCode?: string;
   *     id?: number;
   *     text?: string;
   *   };
   *   cnesCode?: string | null;
   *   teleconsultation: string;
   *   attendanceType: string[];
   *   attendancePrice: number | null;
   *   healthPlans: {
   *     id: number;
   *     name: string | null;
   *   }[] | null;
   *  }[];
   * }}
   */
  async updatePhysicianProfileInfo(profileData) {
    return apiClient.patch('update-physician-profile-info', { ...profileData })
  },
  async createPhysician(info) {
    return apiClient.post('create-physician', { ...info })
  },
  async createEmployee(role, info) {
    return apiClient.post('create-taskforce-person', { ...info, role })
  },
  async editEmployee(personId, info) {
    return apiClient.patch('update-taskforce-person', { ...info, personId })
  },
  async getEmployees(role) {
    return apiClient.get(`get-employees-by-role/${role}`)
  },
  async getTaskforceLink(uuid) {
    return apiClient.get(`get-taskforce-chatbot-link-by-uuid`, { params: { uuid } })
  },
  async createOriginMessage(info) {
    return apiClient.post(`save-pharmaceutical-taskforce-origin-message`, { ...info })
  },
  async removePersonRole(personId, role) {
    return apiClient.post('remove-person-role', { personId, role })
  },
  async removeClinicPhysicianAndPersonRole(personId, clinicId, role) {
    return apiClient.post('remove-clinic-physician-and-person-role', { personId, clinicId, role })
  },
  async inactivateTaskforceLink(clinicLinkId) {
    return apiClient.post('inactivate-taskforce-link', { clinicLinkId })
  },
  async deactivateClinic(clinicId, reason) {
    return apiClient.post('deactivate-clinic', { clinicId, reason })
  },
  async deactivateOriginMessage(originMessageId) {
    return apiClient.post('deactivate-origin-message', { originMessageId })
  },
  async getReportForDownload(period) {
    return apiClient.get('get-reports-for-download', { params: period })
  },
  async getClinicByUser(userId) {
    return apiClient.get(`get-clinic-by-user/${userId}`)
  },
  async getIdentificationExists(number, type, state) {
    return apiClient.get(`get-identification-exists/`, { params: { number, type, state } })
  },
  async acceptClinicTerms(hashedCode, pharmaceuticalId) {
    return apiClient.post(`accept-clinic-terms`, { hashedCode, pharmaceuticalId })
  },
  async uploadLinkImageFile(uuid, file) {
    const formData = new FormData()
    formData.append('image', file)
    return apiClient.post(`link-image-upload/${uuid}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  async getExportableDataToCsv() {
    return apiClient.get(`get-report-from-dashboard-to-csv-download`)
  },
  async saveAnalyticsData(data) {
    return apiClient.post(`save-analytics-data`, { ...data })
  },
  async savePublicPhysicianAndClinic(physicianData, clinicData, pharmaceuticalUuid, clinicUuid) {
    return apiClient.post('save-public-physician-and-clinic', {
      physicianData,
      clinicData,
      pharmaceuticalUuid,
      clinicUuid,
    })
  },
  async getPendingPublicPhysicianAndClinic() {
    return apiClient.get('get-pending-public-physician-and-clinic')
  },
  async updatePhysicianClinicStatus(data) {
    return apiClient.patch('update-physician-clinic-status', { ...data })
  },
  async deleteAndReplacePhysicianClinic(data) {
    return apiClient.post('delete-physician-person', { ...data })
  },
  async unifyDuplicatedPhysicians(data) {
    return apiClient.post('unify-duplicate-physicians', { ...data })
  },

  /**
   *
   * @param {string} uuid
   * @returns {Promise<
   * {
   *   pharmaceuticalId: number;
   *   pharmaceuticalName: string;
   *   projectName: string;
   *   isActive: boolean;
   *   requestPhysicianAttendanceAddress: boolean;
   *  }
   * >}
   */
  async checkPharmaceuticalUuid(uuid) {
    return apiClient.get(`check-pharmaceutical-uuid/${uuid}`)
  },
  async updatePendingClinicInfo(clinicData) {
    return apiClient.post('update-pending-clinic-info', { clinicData })
  },
  async updatePendingPhysicianInfo(physicianData) {
    return apiClient.post('update-pending-physician-info', { physicianData })
  },
  async sendPendingClinicTerms(clinicData) {
    return apiClient.post('send-pending-clinic-terms', { clinicData })
  },
  async checkExistingCnpj(cnpj) {
    return apiClient.get(`check-existing-cnpj/${cnpj}`)
  },
  async savePhysicianAndSendClinicRequest(clinicId, physicianData, pharmaceuticalId) {
    return apiClient.post('save-physician-and-send-clinic-request', { clinicId, physicianData, pharmaceuticalId })
  },
  async getPendingPhysicianInfoByUuid(uuid) {
    return apiClient.get(`get-pending-physician-info-by-uuid/${uuid}`)
  },
  async savePhysicianApprovalByClinic(approved, physicianUuid, clinicUuid) {
    return apiClient.post('save-physician-approval-by-clinic', { approved, physicianUuid, clinicUuid })
  },
  async getTeleconsultationPhysician() {
    return apiClient.get('get-teleconsultation-physician')
  },
  async listTaskforce() {
    return apiClient.get('taskforce-list')
  },
  async newTaskforceAppointment(input) {
    return apiClient.post('new-taskforce-appointment', { ...input })
  },
  /**
   * Cadastra solicitação de interconsulta.
   * @param {{
   * specialtyId: number;
   * attendanceId: number | null;
   * patientId: number | null;
   * }} payload
   * @returns {Promise<void>}
   */
  async newConsultation(payload) {
    return apiClient.post('new-consultation', payload)
  },
  async physicianPatients() {
    return apiClient.get('physician-patients')
  },
  async checkCityByZipCode(zipCode) {
    return apiClient.post(`check-public-city-by-zipcode/${zipCode}`)
  },
  async getPartnerClinics(options) {
    return apiClient.get('get-partner-clinics', { params: options })
  },
  async createPartnerClinicAndLinks(clinicData) {
    return apiClient.post('create-partner-clinic-and-links', { ...clinicData })
  },
  async changePartnerClinicStatuses(clinicId, statusType, status) {
    return apiClient.post('change-partner-clinic-statuses', { clinicId, statusType, status })
  },
  async getNotificationTracking(userId, notificationName) {
    return apiClient.get(`get-notification-tracking/${notificationName}/${userId}`)
  },
  async deliverNotification(userId, notificationId) {
    return apiClient.post(`deliver-notification`, { userId, notificationId })
  },
  async loadClinicProfileData() {
    return apiClient.get(`clinic-profile`)
  },
  async updateClinicConciergeFlag(value) {
    return apiClient.patch(`update-clinic-concierge-flag/${value}`)
  },
  async updateClinicProfile(payload) {
    return apiClient.patch(`update-clinic-profile`, payload)
  },
  async updatePatient(data) {
    return apiClient.patch(`update-patient`, data)
  },
  /**
   *
   * @param {{
   * clinicId?: number;
   * physicianId?: number;
   * id: number;
   * name?: string;
   * email?: string;
   * birthday?: string;
   * cpf?: string;
   * motherName?: string;
   * currentWeightKg?: number;
   * currentHeightCm?: number;
   * assignedSex?: EAssignedSex;
   * skinColor?: ESkinColor;
   * mobile?: string;
   * address?: AddressDto;
   * wholeAddress?: boolean;
   * hasHealthPlan?: boolean;
   * healthPlan?: string | HealthPlanDto;
   * zipCode?: string | null;
   * }} data
   * @returns {Promise<void>}
   */
  async partialUpdatePatient(data) {
    return apiClient.patch(`partial-update-patient`, data)
  },
  async revokePendingClinic(pendingClinicId) {
    return apiClient.patch(`revoke-pending-clinic/${pendingClinicId}`)
  },
  async listTaskforces(params) {
    return apiClient.get('list-taskforces', { params })
  },
  async listPatientAttendanceExamsCompact(patientId, listOptions) {
    return apiClient.get(`get-patient-attendance-exams-compact/${patientId}`, {
      params: listOptions,
    })
  },
  async changeTaskforceClinicSanofi(payload) {
    return apiClient.patch('change-taskforce-clinic', payload)
  },
  async saveAttendanceByClinic(attendanceData, fillerName) {
    return apiClient.post('save-attendance-by-clinic', { attendanceData, fillerName })
  },
  async getTaskforceAttendance(taskforceAppointmentId) {
    return apiClient.get(`get-taskforce-attendance/${taskforceAppointmentId}`)
  },
  async createAttendanceNote(payload) {
    return apiClient.post('create-attendance-note', payload)
  },
  async associateAttendanceNote(noteId, payload) {
    return apiClient.patch(`associate-attendance-note/${noteId}`, payload)
  },
  /**
   * @param {number} physicianId
   * @param {number} patientId
   * @returns {Promise<void>}
   */
  async linkPatientToPhysician(physicianId, patientId) {
    return apiClient.post('link-existing-patient-to-physician', {
      physicianId,
      patientId,
    })
  },
  async deleteAttendanceNote(noteId) {
    return apiClient.delete(`delete-attendance-note/${noteId}`)
  },
  /**
   *
   * @param {number} patientId
   * @returns {Promise<
   * {
   *   id: number;
   *   content: string;
   *   patientId: number;
   *   physicianId: number;
   *   attendanceId: number | null;
   *   attendance: {
   *     id: number,
   *     heightCm: number | null,
   *     weightKg: number | null,
   *     healthPlan: string | null,
   *     hypothesis: string | null,
   *     report: string | null,
   *     references: string | null,
   *     note: string | null,
   *     uploadPhotoUuid: string,
   *     remote: boolean,
   *     retroactiveEntry: boolean | null,
   *     physicianId: number,
   *     patientId: number,
   *     diseaseId: number,
   *     pharmaceuticalId: number,
   *     data: ObjectLiteral | null,
   *     filledBy: string | null,
   *     summary: ObjectLiteral | null,
   *     attendanceFormId: number | null,
   *     guidedAttendanceFormId: number | null,
   *     startedAt: Date,
   *     createdAt: Date,
   *     updatedAt: Date,
   *     deletedAt: Date | null,
   *   };
   *   createdAt: Date;
   *   updatedAt: Date;
   *   deletedAt: Date | null;
   *  }[];
   * >}
   */
  async getAttendanceNotes(patientId) {
    return apiClient.get(`get-attendance-notes/${patientId}`)
  },
  async checkExistingLogin(email) {
    return apiClient.get(`check-existing-login/${email}`)
  },
  async saveAttendanceAnswers(attendanceId, answers, parameters, extraAnswers) {
    return apiClient.post('save-attendance-answers', { attendanceId, answers, parameters, extraAnswers })
  },
  async newEcPendingPhysician(payload) {
    const formData = new FormData()
    formData.append('channels', payload.channels)
    formData.append('file', payload.file)
    formData.append('date', payload.date)
    formData.append('consultor', payload.consultor)
    formData.append('name', payload.name)
    formData.append('login', payload.login)
    formData.append('whatsapp', payload.whatsapp)
    formData.append('crmNumber', payload.crmNumber)
    formData.append('crmState', payload.crmState)
    formData.append('specialties', JSON.stringify(payload.specialties))

    return apiClient.post('ec-new-pending-physician', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  async getAttendanceForm(attendanceId) {
    return apiClient.get(`get-attendance-form`, {
      params: {
        attendanceId,
      },
    })
  },
  async getLatestAttendanceFormByDisease(diseaseId) {
    return apiClient.get(`get-latest-attendance-form-by-disease/${diseaseId}`)
  },

  async getGuidedAttendanceFormByDisease(diseaseId, version) {
    return apiClient.get(`guided-attendance-form/by-disease/${diseaseId}`, {
      params: {
        version,
      },
    })
  },
  async getAttendanceAnswers(attendanceId) {
    return apiClient.get(`get-attendance-answers`, {
      params: {
        attendanceId,
      },
    })
  },
  async getAttendanceParameterAnswers(attendanceId) {
    return apiClient.get(`get-attendance-parameter-answers`, {
      params: {
        attendanceId,
      },
    })
  },
  async duplicateAttendance(attendance) {
    return apiClient.post('duplicate-attendance', { ...attendance })
  },
  /**
   *
   * @param {number} limit
   * @returns {Promise<{ id: number, name: string, state: string, stateId: number, uf: string,ibgeCode:string}[]>}
   */
  async searchAllCities(limit = 10) {
    return apiClient.get(`search-city`, {
      params: {
        limit,
      },
    })
  },
  /**
   *
   *  @param {string} search
   *  @returns {Promise<{ id: number, name: string, state: string, stateId: number, uf: string,ibgeCode:string}[]>}
   */
  async searchCity(search, limit = 10) {
    let city = search ?? ''

    let state = ''
    if (typeof search === 'string' && search.includes('/')) {
      city = search.split('/')[0]
      state = search.split('/')[1]
    }

    return apiClient.get(`search-city/${city}`, {
      params: {
        state,
        limit,
      },
    })
  },
  /**
   * @param {unknown} payload
   * @returns {Promise<{pendingPhysicianId: number, pendingPhysicianClinicId: number}>}
   */
  async newPhysician(payload) {
    return apiClient.post('register-physician', payload)
  },
  async listPhysiciansByAddress(pharmaceuticalId, params) {
    return apiClient.get(`list-physicians-by-address/${pharmaceuticalId}`, { params })
  },
  /**
   *
   * @param {string|string[]} parameterSlug
   * @returns {Promise<{[key: string]: unknown}>}
   */
  async getParameter(parameterSlug) {
    if (Array.isArray(parameterSlug)) {
      parameterSlug = parameterSlug.join(',')
    }

    return apiClient.get(`get-parameter/${parameterSlug}`)
  },

  async getParameterWithoutLogin(parameterSlug, pharmaceuticalId) {
    if (Array.isArray(parameterSlug)) {
      parameterSlug = parameterSlug.join(',')
    }

    return apiClient.get(`get-parameter/${pharmaceuticalId}/${parameterSlug}`)
  },

  async upsertPartialPatient(data) {
    return apiClient.post(`upsert-partial-patient`, data)
  },

  async printDotPrescription(patientId, templateId, prescription, variables) {
    return apiClient.post(
      `print-dot-prescription`,
      { patientId, templateId, prescription, variables },
      { responseType: 'blob' },
    )
  },
  /**
   * @param {string} uuid
   * @return {Promise<{pharmaceuticalId: number}>}
   *
   */
  async getPharmaceuticalDataByUuid(uuid) {
    return apiClient.get(`get-pharmaceutical-data/${uuid}`)
  },
  async getPharmaceuticalLayout(path, pharmaceuticalId) {
    return apiClient.get(`get-pharmaceutical-layout/`, { params: { path, pharmaceuticalId } })
  },

  async userHasPassword(login) {
    return apiClient.get('user-has-password', {
      params: { login },
    })
  },

  async generateResetPasswordCode() {
    return apiClient.patch('generate-reset-password-code')
  },

  async getPhysicianTaskforceData(personUuid, password) {
    return apiClient.post('get-physician-taskforce-data', { personUuid, password })
  },
  /**
   * Returns the dashboard data
   * @param {string[]} dateRange
   * @param {number} clinicId
   * @returns {Promise<GetShortwayDashboardOutputDto>} dashboardData
   */
  async getShortwayDashboard(dateRange, filterClinicIds) {
    return apiClient.get('get-shortway-dashboard', { params: { dateRange, filterClinicIds } })
  },

  async listClinicsByPharmaceutical(pharmaceuticalNames) {
    if (!Array.isArray(pharmaceuticalNames)) {
      pharmaceuticalNames = [pharmaceuticalNames]
    }
    return apiClient.get('list-clinics-by-pharmaceutical', {
      params: { pharmaceuticalNames: JSON.stringify(pharmaceuticalNames) },
    })
  },
  async createPhysicianCnes(data) {
    return apiClient.post('create-physician-cnes', data)
  },

  /**
   * @param {string} type
   * @returns {Promise<{value: string}>}
   */
  async getPersonIdentification(type) {
    return apiClient.get(`get-person-identification/${type}`)
  },

  /**
   * @param {string} search
   * @returns {Promise<{items: {id: number, name: string}[], total: number}>}
   */
  async listDiseases(search) {
    return apiClient.get(`disease`, {
      params: { search },
    })
  },

  /**
   * @param {string} name
   * @returns {Promise<{id: number, name: string}>}
   */
  async getDiseaseByName(name) {
    return apiClient.get(`get-disease-by-name/${name}`)
  },
  /**
   * @param {{surveyId: number, data: unknown, userId?: number, pendingPhysicianId?: number}}
   * @returns {Promise<void>}
   */
  async saveSimpleSurvey({ surveyId, data, userId, pendingPhysicianId }) {
    return apiClient.post('save-simple-survey', { surveyId, data, userId, pendingPhysicianId })
  },

  /**
   * @param {string} label
   * @param {number} pharmaceuticalId
   * @returns {Promise<{id: number}>}
   */
  async getSurveyByLabel(label, pharmaceuticalId) {
    return apiClient.get(`get-survey-by-label/${pharmaceuticalId}/${label}`)
  },
  /**
   *
   * @param {number} patientId
   * @param {{page?: number, itemsPerPage?: number, filters?: Record<string, any>, sort?: {by: string, ascending?: boolean}}} options
   * @returns {Promise<{items: any[], count: number, availableFilters?: Record<string, {text: string, value: string | number | boolean}[]>, availableSorters?: string[]}>}
   */
  async getAttendancesByPatient(patientId, options = {}) {
    return apiClient.get(`get-attendances-by-patient/${patientId}`, {
      params: {
        ...options,
        filters: options.filters ? JSON.stringify(options.filters) : undefined,
        sort: options.sort ? JSON.stringify(options.sort) : undefined,
      },
    })
  },

  async reactivateClinic(clinicId, withLinks) {
    return apiClient.post('reactivate-clinic', { clinicId, withLinks })
  },
  async updateParameter(data) {
    return apiClient.patch('update-parameter', data)
  },
  async deleteParameter(parameterId) {
    return apiClient.post('delete-parameter', { parameterId })
  },
  async getPharmaceuticalsParameters() {
    return apiClient.get('get-pharmaceuticals-parameters')
  },
  async createParameterKind(data) {
    return apiClient.post('create-parameter-kind', { data })
  },
  async getPharmaceuticals() {
    return apiClient.get('pharmaceutical')
  },
  async addParameterToPharmaceutical(data) {
    return apiClient.post('add-parameter-to-pharmaceutical', { data })
  },
  /**
   *
   * @param {string} search?
   * @param {number} take
   * @param {string} sort
   * @returns {Promise<{id:number;name:string}[]>}
   */
  async generalSpecialties(search = '', take = 0, sort = undefined) {
    const specialties = apiClient.get(`options/general-specialty`, {
      params: { search, take },
    })

    if (!sort) {
      return specialties
    }

    return specialties.then((specialties) => {
      return specialties.sort((a, b) => {
        if (sort === 'asc') {
          return a.name.localeCompare(b.name)
        }
        return b.name.localeCompare(a.name)
      })
    })
  },
  /**
   *
   * @param {number} userId
   * @param {string} sentTo
   * @param {string} pharmaceuticalName
   */
  async sendVerificationCode(userId, sentTo, pharmaceuticalName) {
    return apiClient.post(`send-verification-code`, { userId, sentTo, pharmaceuticalName })
  },
  /**
   *
   * @param {number} userId
   * @param {string} code
   * @returns {Promise<{isCodeValid:boolean}>}
   */
  async checkVerificationCode(userId, code) {
    return apiClient.get(`check-verification-code?userId=${userId}&code=${code}`)
  },

  /**
   * @typedef UserDto
   * @property {string} name
   * @property {string} [mobile]
   * @property {string} email
   * @property {number} id
   */

  /**
   * @typedef CheckUserExistsOutputDto
   * @property {boolean} exists
   * @property {UserDto} [user]
   */

  /**
   * Verifica se um usuário existe.
   * @param {{number: string;uf: string;}[]} crms
   * @param {string} email
   * @param {string} mobile
   * @returns {Promise<CheckUserExistsOutputDto>} Uma Promise que resolve para um objeto indicando se o usuário existe e, se existir, seus dados.
   */
  async checkUserExists(email, mobile, crms) {
    return apiClient.post(`check-user-exists`, { email, mobile, crms })
  },
  /**
   *
   * @param {formData: {
   *  id: number;
   *  name?: string;
   *  email?: string;
   *  gender: string | null;
   *  mobile?: string;
   *  birthday: string | null;
   *  crms: {
   *   number: string;
   *   state: string | null;
   *  }[];
   *  cns?: string;
   *  specialties: {
   *   specialtyId: number;
   *   specialty: {
   *    id: number
   *    specialtyName: string
   *    subspecialtyName: string | null
   *    createdAt: string
   *    updatedAt: string
   *    deletedAt: string | null
   *   };
   *   rqe: string | null;
   *  }[] | null;
   *  attendanceAddresses?: {
   *   name: string;
   *   zipCode: string | null;
   *   mobile: string | null;
   *   phone: string | null;
   *   street: string;
   *   district: string;
   *   number: string;
   *   complement: string | null;
   *   cityId: number;
   *   city: {
   *     ibgeCode?: string;
   *     id?: number;
   *     text?: string;
   *   };
   *   cnesCode?: string | null;
   *   teleconsultation: string;
   *   attendanceType: string[];
   *   attendancePrice: number | null;
   *   healthPlans: {
   *     id: number;
   *     name: string | null;
   *   }[] | null;
   *  }[];
   * }}
   * @param {number} pharmaceuticalId
   * @returns {Promise<{
   * isProjectActive: boolean;
   * login: boolean
   * sendEmail: boolean
   * codeToLogin: number
   * }>}
   */
  async saveUserInfo(formData, pharmaceuticalId) {
    return apiClient.post(`save-user-info`, { user: formData, pharmaceuticalId })
  },
  /**
   *
   * @param {string} search?
   * @param {number} take
   * @param {string} sort
   * @returns {Promise<{id: number, rqe: string | null, specialtyName: string, subspecialtyName: string | null }[]>}
   */
  async getSpecialties(search = '', take = 0, sort = undefined) {
    const specialties = apiClient.get(`options/specialty`, {
      params: { search, take },
    })

    if (!sort) {
      return specialties
    }

    return specialties.then((specialties) => {
      return specialties.sort((a, b) => {
        if (sort === 'asc') {
          return a.name.localeCompare(b.name)
        }
        return b.name.localeCompare(a.name)
      })
    })
  },

  /**
   *
   * @param {number} userId
   * @returns {Promise<{
   *  id: number;
   *  name?: string;
   *  email?: string;
   *  gender: 'M' | 'F' | 'N' | null;
   *  mobile?: string;
   *  birthday: string | null;
   *  crms: {
   *   number: string;
   *   state: string | null;
   *  }[];
   *  cns?: string;
   *  specialties: {
   *   specialtyId: number;
   *   specialty: {
   *    id: number
   *    specialtyName: string
   *    subspecialtyName: string | null
   *    createdAt: string
   *    updatedAt: string
   *    deletedAt: string | null
   *   };
   *   rqe: string | null;
   *  }[];
   *  attendanceAddresses: {
   *   name: string;
   *   zipCode: string | null;
   *   whatsapp: string | null;
   *   phone: string | null;
   *   isWhatsapp: boolean;
   *   street: string;
   *   district: string;
   *   number: string;
   *   complement: string | null;
   *   cityId: number;
   *   city: {
   *     id: number;
   *     ibgeCode: string;
   *     text: string;
   *   };
   *   cnesCode?: string | null;
   *   teleconsultation:  ETeleconsultationOptions;
   *   attendanceType: EAttendanceType[];
   *   attendancePrice: number | null;
   *   healthPlans: {
   *     id: number;
   *     name: string;
   *   }[] | null;
   *  }[];
   * removedAddresses:{
   *  id: number;
   *  name: string;
   * }[]
   * }>}
   */

  async getExistingUserInfo(userId) {
    return apiClient.get(`get-existing-user-info?userId=${userId}`)
  },
  /**
   *
   * @param {string} name
   * @returns {Promise<{uuid: string, id: number}>}
   */
  async getPharmaceuticalByName(name) {
    return apiClient.get(`get-pharmaceutical-by-name?pharmaceuticalName=${name}`)
  },
  /**
   *
   * @param {{
   *   attendanceId: number;
   *   templateId: number;
   *   isCustomPrescription: boolean;
   * }} data
   * @returns
   */
  async changeOldPrescriptionTemplate(data) {
    return apiClient.post('change-old-prescription-template', data)
  },

  attendance: attendanceEndpoints(apiClient),
  clinicTaskforceLink: clinicTaskforceLinkEndpoints(apiClient),
  clinic: clinicEndpoints(apiClient),
  disease: diseaseEndpoints(apiClient),
  medicalDocument: medicalDocumentEndpoints(apiClient),
  metabaseQuestion: metabaseQuestionEndpoints(apiClient),
  patient: patientEndpoints(apiClient),
  parameter: parameterEndpoints(apiClient),
  printingTemplate: printingTemplateEndpoints(apiClient),
  taskforce: taskforceEndpoints(apiClient),
  treatment: treatmentEndpoints(apiClient),
  physician: physicianEndpoints(apiClient),
  kba: kbaEndpoints(apiClient),
  sendEmail: senEmailEndpoints(apiClient),
  validation: validationsEndpoints(apiClient),
  lme: lmeEndpoints(apiClient),
  address: addressEndpoints(apiClient),
  guidedAttendanceForm: attendanceFormEndpoints(apiClient),
  calculator: CalculatorsEndpoints(apiClient),
  term: termEndpoints(apiClient),
  cpf: cpfEndpoints(apiClient),
  auth: authEndpoints(apiClient),
}
const am4 = am4core
const amcharts4 = am4charts
const ref = {
  chartdiv: 'chartdiv',
  series: [
    {
      type: 'PieSeries',
      dataFields: {
        value: 'litres',
        category: 'country',
      },
    },
  ],
}

export default {
  install(VueInstance, options) {
    VueInstance.prototype.$api = routes
    VueInstance.prototype.$am4core = am4
    VueInstance.prototype.$am4charts = amcharts4
    VueInstance.prototype.$refs = ref
  },
}

export function useApi() {
  return routes
}
